import React, { useContext, useState } from "react";

import { Link } from "react-router-dom";

import {
  Button,
  Container,
  CopyButton,
  Grid,
  Modal,
  Pagination,
  Paper,
  Table,
  TableThProps,
  Text,
  Tooltip,
} from "@mantine/core";
import dayjs from "dayjs";

import DisplayAlert from "../../Components/DisplayAlert";
import MegaLoader from "../../Components/MegaLoader";

import { AppContext } from "../Wrapper";

import { AppName, BrandImages, Colors } from "../../Lib/Data";
import { Endpoints } from "../../Lib/Endpoints";
import {
  generateRandomString,
  getEllipsisFromString,
  getFinancialValueFromNumeric,
  getPaginationTotal,
} from "../../Lib/Methods";
import { PaginatedResponse } from "../../Lib/Responses";
import { PaginationProps, TransactionProps } from "../../Lib/Types";
import { usePerformRequest } from "../../Lib/usePerformRequest";

import { useDisclosure } from "@mantine/hooks";
import "./styles.scss";

const MyTransactions: React.FC = () => {
  const context = useContext(AppContext);
  const theme = context?.theme ?? "light";

  document.title = `Transaction History | ${AppName}`;

  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });
  const {
    data: data,
    isLoading: isLoadingTransactions,
    reloadData: reloadFromServer,
  } = usePerformRequest<PaginatedResponse<TransactionProps[]>>({
    method: "GET",
    route: Endpoints.GetUserTransactionsWithTemps(
      pagination.page,
      pagination.limit
    ),
  });

  const [
    isTransactionModalOpened,
    { open: OpenTransactionModal, close: CloseTransactionModal },
  ] = useDisclosure(false);

  const [currentTransaction, setCurrentTransaction] =
    useState<TransactionProps>();

  const tableRowHeadProps: TableThProps = {
    style: {
      backgroundColor: theme === "dark" ? "transparent" : Colors.White,
      color: theme === "dark" ? Colors.White : Colors.BlackPrimary,
      borderRight: "1px solid #E0E0E0",
    },
  };
  return (
    <Container size="md">
      <div className="my-transactions-container">
        <Text
          fw={500}
          mb={30}
          c={
            theme === "dark"
              ? Colors.WhiteBlueTransparent
              : Colors.GreenSecondary
          }
        >
          All Transactions
        </Text>
        {isLoadingTransactions ? (
          <MegaLoader />
        ) : (
          <>
            {data ? (
              <>
                {data.data && data.data.length > 0 ? (
                  <>
                    {currentTransaction && (
                      <Modal
                        opened={isTransactionModalOpened}
                        onClose={() => {
                          setCurrentTransaction(undefined);
                          CloseTransactionModal();
                        }}
                        title="Transaction Details"
                        centered
                        withCloseButton={true}
                        size="md"
                        padding={0}
                        radius="lg"
                        styles={{
                          header: {
                            backgroundColor:
                              theme === "dark"
                                ? "var(--mantine-color-dark-7)"
                                : Colors.White,
                            color:
                              theme === "dark"
                                ? Colors.White
                                : Colors.BlackPrimary,
                            padding: "20px 24px",
                            borderBottom: `1px solid ${
                              theme === "dark"
                                ? "var(--mantine-color-dark-6)"
                                : "var(--mantine-color-gray-2)"
                            }`,
                          },
                          body: {
                            padding: 0,
                          },
                          content: {
                            overflow: "hidden",
                          },
                        }}
                      >
                        <div
                          className={`transaction-modal transaction-modal-${theme}`}
                        >
                          <Table
                            variant="vertical"
                            layout="fixed"
                            withTableBorder
                          >
                            <Table.Thead>
                              <Table.Tr key={generateRandomString(12)}>
                                <Table.Th {...tableRowHeadProps} w={110}>
                                  Reference
                                </Table.Th>
                                <Table.Td>
                                  <CopyButton
                                    value={currentTransaction.reference}
                                  >
                                    {({ copied, copy }) => (
                                      <Tooltip
                                        label={
                                          copied
                                            ? "Copied!"
                                            : "Copy to clipboard"
                                        }
                                      >
                                        <div
                                          className="pointer flex-row align-center"
                                          onClick={copy}
                                          style={{
                                            color: copied
                                              ? Colors.GreenPrimary
                                              : Colors.BluePrimary,
                                            height: "100%",
                                          }}
                                        >
                                          {currentTransaction.reference}
                                        </div>
                                      </Tooltip>
                                    )}
                                  </CopyButton>
                                </Table.Td>
                              </Table.Tr>
                              <Table.Tr key={generateRandomString(12)}>
                                <Table.Th {...tableRowHeadProps}>Date</Table.Th>
                                <Table.Td
                                  c={
                                    theme === "dark"
                                      ? Colors.White
                                      : Colors.GraySecondary
                                  }
                                >
                                  {dayjs(currentTransaction.createdAt).format(
                                    "DD MMM YYYY - hh:mmA"
                                  )}
                                </Table.Td>
                              </Table.Tr>
                              <Table.Tr key={generateRandomString(12)}>
                                <Table.Th {...tableRowHeadProps}>
                                  Amount
                                </Table.Th>
                                <Table.Td
                                  fw={600}
                                  c={Colors.GreenSecondary}
                                  fz={17}
                                >
                                  $
                                  {getFinancialValueFromNumeric(
                                    currentTransaction.amount
                                  )}
                                </Table.Td>
                              </Table.Tr>
                              <Table.Tr key={generateRandomString(12)}>
                                <Table.Th {...tableRowHeadProps}>
                                  Service(s)
                                </Table.Th>
                                <Table.Td>
                                  <div className="flex-row align-center temp-service-images">
                                    {currentTransaction.services.map((s) => (
                                      <img
                                        src={BrandImages.retrieveImage(
                                          s,
                                          theme
                                        )}
                                        alt=""
                                        className="transaction-image-row"
                                      />
                                    ))}
                                  </div>
                                </Table.Td>
                              </Table.Tr>

                              <Table.Tr key={generateRandomString(12)}>
                                <Table.Th {...tableRowHeadProps}>
                                  Duration
                                </Table.Th>
                                <Table.Td
                                  c={
                                    theme === "dark"
                                      ? Colors.White
                                      : Colors.GraySecondary
                                  }
                                >
                                  {currentTransaction.duration}
                                </Table.Td>
                              </Table.Tr>
                            </Table.Thead>
                          </Table>
                          <div className="flex-row align-center">
                            <Button
                              variant="outline"
                              color={Colors.GreenPrimary}
                              onClick={() => {
                                CloseTransactionModal();
                                setCurrentTransaction(undefined);
                              }}
                              w={120}
                              mt={20}
                            >
                              Close
                            </Button>
                            <Link
                              to={`/d/support/new?transaction=${currentTransaction.id}`}
                            >
                              <Button ml={10} mt={20}>
                                Dispute
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </Modal>
                    )}
                    <Grid gutter={4}>
                      {data?.data.map((transaction) => {
                        return (
                          <Grid.Col
                            span={{ base: 12, sm: 6, lg: 6 }}
                            key={transaction.id}
                          >
                            <Paper
                              onClick={() => {
                                setCurrentTransaction(transaction);
                                OpenTransactionModal();
                              }}
                              withBorder
                              className={`transaction pointer transaction-${theme}`}
                            >
                              <div className="content flex-col justify-between width-100">
                                <div className="flex-row align-center justify-between width-100">
                                  <div className="flex-col">
                                    <Text
                                      c={
                                        theme === "dark"
                                          ? Colors.WhiteBlueTransparent
                                          : Colors.GrayPrimary
                                      }
                                      mb={-10}
                                      fz={14}
                                    >
                                      Reference
                                    </Text>
                                    <Text
                                      className="pointer"
                                      fw={500}
                                      fz={19}
                                      w="fit-content"
                                      c={Colors.BluePrimary}
                                    >
                                      {getEllipsisFromString(
                                        transaction.reference,
                                        10
                                      )}
                                    </Text>
                                  </div>
                                  <div className="flex-col align-end">
                                    <Text
                                      c={
                                        theme === "dark"
                                          ? Colors.White
                                          : Colors.GrayPrimary
                                      }
                                      fz={14}
                                    >
                                      {dayjs(transaction.createdAt).format(
                                        "ddd, DD MMM YYYY"
                                      )}
                                    </Text>
                                    <Text c={Colors.GrayPrimary} fz={14}>
                                      <b>at</b>&nbsp;
                                      {dayjs(transaction.createdAt).format(
                                        "hh:mm A"
                                      )}
                                    </Text>
                                  </div>
                                </div>
                                <div className="flex-row align-center width-100 justify-between">
                                  <Text
                                    fw={500}
                                    c={Colors.GreenPrimary}
                                    fz={20}
                                  >
                                    $
                                    {getFinancialValueFromNumeric(
                                      transaction.amount
                                    )}
                                  </Text>
                                  <div className="flex-row align-center">
                                    {transaction.services.map((s, index) => (
                                      <img
                                        key={index}
                                        src={BrandImages.retrieveImage(
                                          s,
                                          theme
                                        )}
                                        alt=""
                                        className="transaction-image-row"
                                      />
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </Paper>
                          </Grid.Col>
                        );
                      })}
                    </Grid>
                    <div className="flex-row align-center justify-center width-100">
                      <Pagination
                        value={pagination.page}
                        onChange={(value) => {
                          setPagination({ ...pagination, page: value });
                        }}
                        my={40}
                        disabled={
                          data?.data.length === 0 || isLoadingTransactions
                        }
                        total={getPaginationTotal(
                          data?.totalRecords,
                          pagination.limit
                        )}
                      />
                    </div>
                  </>
                ) : (
                  <DisplayAlert
                    title="No Transactions Found"
                    children={
                      <div className="flex-col">
                        <Text>
                          {context?.profile && context.profile.id
                            ? "You have no previous transactions!"
                            : "You must log in first!"}
                        </Text>
                        <Link
                          to={
                            context?.profile && context.profile.id
                              ? "/d/services"
                              : "/login"
                          }
                        >
                          <Button mt={10} color={Colors.GreenPrimary}>
                            {context?.profile && context.profile.id
                              ? "Purchase Service"
                              : "Login"}
                          </Button>
                        </Link>
                      </div>
                    }
                  />
                )}
              </>
            ) : (
              <DisplayAlert
                title="No Transactions Found"
                children={
                  <div className="flex-col">
                    <Text>
                      {context?.profile && context.profile.id
                        ? "You have no previous transactions!"
                        : "You must log in first!"}
                    </Text>
                    <Link
                      to={
                        context?.profile && context.profile.id
                          ? "/d/services"
                          : "/login"
                      }
                    >
                      <Button mt={10} color={Colors.GreenPrimary}>
                        {context?.profile && context.profile.id
                          ? "Purchase Service"
                          : "Login"}
                      </Button>
                    </Link>
                  </div>
                }
              />
            )}
          </>
        )}
      </div>
    </Container>
  );
};
export default MyTransactions;
